import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { useId, useMemo, useEffect, useCallback } from 'react';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { B as Box } from '../Box/Box.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';
import { u as useOverlaysStore, f as UPDATE_POPOVER_PROPS, e as OPEN_POPOVER, d as CLOSE_POPOVER } from '../../providers/overlaysContext/overlaysContext.js';
import { useForwardLocalDomRef } from '../../hooks/useForwardLocalDomRef.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';
import { useControlledOverlay } from '../../hooks/useControlledOverlay.js';

var _excluded$1 = ["children", "domRef"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function PopoverContent(_ref) {
  var children = _ref.children,
    domRef = _ref.domRef,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return jsx(Box, _objectSpread$2(_objectSpread$2({}, props), {}, {
    domRef: domRef,
    children: children
  }));
}
PopoverContent.displayName = "Popover.Content";

var _excluded = ["children"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function PopoverTarget(_ref) {
  var children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  return jsx(SmartClone, _objectSpread$1(_objectSpread$1({}, props), {}, {
    children: children
  }));
}
PopoverTarget.displayName = "Popover.Target";

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Popover(_ref) {
  var visible = _ref.visible,
    idProp = _ref.id,
    children = _ref.children,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? {
      x: "center",
      y: "below"
    } : _ref$position,
    _ref$domRef = _ref.domRef,
    domRef = _ref$domRef === void 0 ? {
      current: null
    } : _ref$domRef,
    onOutsideClick = _ref.onOutsideClick,
    _ref$verticalGap = _ref.verticalGap,
    verticalGap = _ref$verticalGap === void 0 ? 4 : _ref$verticalGap;
  var localDomRef = useForwardLocalDomRef(domRef);
  var fallbackId = useId();
  var id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  var target = useGetSubcomponentChild(children, PopoverTarget);
  var content = useGetSubcomponentChild(children, PopoverContent);
  var _useOverlaysStore = useOverlaysStore(function (state) {
      return state.popoverList;
    }),
    popoverList = _useOverlaysStore.state,
    dispatchAction = _useOverlaysStore.dispatchAction;
  var isControlled = useMemo(function () {
    return typeof visible !== "undefined";
  }, [visible]);
  var popoverProps = useMemo(function () {
    return {
      id: id,
      content: content,
      targetRef: localDomRef,
      position: position,
      isControlled: isControlled,
      onOutsideClick: onOutsideClick,
      verticalGap: verticalGap
    };
  }, [content, id, isControlled, localDomRef, onOutsideClick, position, verticalGap]);

  // @NOTE: this useEffect allows the Popover props to stay up to date,
  // as the application re-renders and things potentially change
  useEffect(function () {
    dispatchAction({
      type: UPDATE_POPOVER_PROPS,
      payload: popoverProps
    });
  }, [dispatchAction, popoverProps]);
  var showPopover = useCallback(function () {
    dispatchAction({
      type: OPEN_POPOVER,
      payload: popoverProps
    });
  }, [dispatchAction, popoverProps]);
  var hidePopover = useCallback(function () {
    dispatchAction({
      type: CLOSE_POPOVER,
      payload: {
        id: id
      }
    });
  }, [dispatchAction, id]);

  // @NOTE: Allow popover to be controlled:
  useControlledOverlay({
    id: id,
    visible: visible,
    showFunction: showPopover,
    hideFunction: hidePopover,
    overlayList: popoverList
  });
  var onClickProps = useMemo(function () {
    return !isControlled ? {
      onClick: function onClick() {
        if (popoverList.find(function (popover) {
          return popover.id === id;
        })) {
          hidePopover();
        } else {
          showPopover();
        }
      }
    } : {};
  }, [popoverList, isControlled, id, hidePopover, showPopover]);
  return target ? jsx(SmartClone, _objectSpread(_objectSpread({}, onClickProps), {}, {
    id: id,
    domRef: localDomRef,
    children: target
  })) : null;
}
Popover.displayName = "Popover";
Popover.Target = PopoverTarget;
Popover.Content = PopoverContent;

export { Popover as P };
//# sourceMappingURL=Popover.js.map
